import EditorTestCasesItem from "./testCasesItem";
import Tooltip from "@lxp-app/components/atoms/tooltip";
import { useTranslation } from "react-i18next";

import { cn } from "../../../../../../../../utils/cn";
import GetHeroIcon from "@assessment/utils/getHeroIcons";
import { useEffect, useState } from "react";
import { useAppSelector } from "@assessment/store/store";
import * as HeroIconsSolid from "@heroicons/react/24/solid";
import * as HeroIconsOutline from "@heroicons/react/24/outline";
import LoaderIcon from "@assessment/components/atoms/loaderIcon";
interface Props {
  editorTheme: "vs-dark" | "light";
  testCaseResult: any;
  showLoader: boolean;
  setShowLoader: (arg: boolean) => void;
  testcaseType: "sample" | "hidden";
  runTimeError: string;
}

interface StatusIcon {
  iconName: keyof typeof HeroIconsSolid | keyof typeof HeroIconsOutline;
  status: string;
  color: string;
  variant?: "solid" | "outline";
  tooltipMessage?: string;
}

const EditorTestCases = ({
  editorTheme,
  testCaseResult,
  showLoader,
  setShowLoader,
  testcaseType,
  runTimeError
}: Props) => {
  const { t } = useTranslation();
  const testCases = useAppSelector((state) => state.AssessmentSlice.testCases);
  const sampleTestCases = testCases?.sampleTestCases || [];
  const hiddenTestCaseIds = testCases?.hiddenTestCases || [];
  const [selectedTestCase, setSelectedTestCase] = useState<any>({});

  useEffect(() => {
    setSelectedTestCase(sampleTestCases?.[0]);
  }, [sampleTestCases]);

  useEffect(() => {
    if (testCaseResult && Object.keys(testCaseResult)?.length > 0)
      setShowLoader(false);
  }, [testCaseResult]);

  const statusIconMap: StatusIcon[] = [
    {
      iconName: "CheckIcon",
      status: "PASSED",
      color: "text-[#00875D]",
      variant: "solid",
      tooltipMessage: t("assessment.coding.testCasePassed")
    },
    {
      iconName: "XMarkIcon",
      status: "FAILED",
      color: "text-red-800",
      tooltipMessage: t("assessment.coding.testCaseFailed"),
      variant: "solid"
    },
    {
      iconName: "ExclamationTriangleIcon",
      status: "RUNTIME_ERROR",
      color: "text-yellow-500",
      variant: "outline",
      tooltipMessage: t("assessment.coding.runtimeError")
    },
    {
      iconName: "ClockIcon",
      status: "TIMEOUT",
      color: "text-yellow-500",
      variant: "outline",
      tooltipMessage: t("assessment.coding.timeLimitExceeded")
    }
  ];

  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-col pt-2 rounded-2xl",
        editorTheme === "vs-dark" && "border-gray-500 text-white",
        editorTheme === "light" && "border-neutral-800 text-neutral-800"
      )}
    >
      {/* TODO: Add Tabs */}
      <div className="flex items-center justify-start gap-6 p-3">
        <span className="text-base">{t("assessment.coding.testCases")}</span>
      </div>

      {/* Content */}
      <div
        className={cn(
          "flex h-full min-h-0 flex-col border-t lg:flex-row",
          editorTheme === "vs-dark" && "border-gray-500 text-white",
          editorTheme === "light" &&
            "border-gray-200 dark:border-neutral-800 text-neutral-800"
        )}
      >
        <>
          {testcaseType == "sample" ? (
            <>
              {/* Right */}
              <div className="w-full min-h-0 flex-[0_0_38%] space-y-3 overflow-y-auto px-2 py-3 thin-scrollbar lg:w-[12rem] lg:flex-[0_0_12rem]">
                {sampleTestCases &&
                  sampleTestCases?.length > 0 &&
                  sampleTestCases.map((testcase: any, index: number) => (
                    <div
                      className={`flex justify-between items-center w-40 gap-1.5 px-5 py-1 rounded-md  cursor-pointer
                ${testcase?.id == selectedTestCase?.id && editorTheme === "vs-dark" && "bg-slate-800"}
                ${testcase?.id == selectedTestCase?.id && editorTheme === "light" && "bg-gray-100"}
                `}
                      onClick={() => setSelectedTestCase(testcase)}
                    >
                      {showLoader &&
                        Object.keys(testCaseResult)?.length == 0 && (
                          <div>
                            <LoaderIcon />
                          </div>
                        )}
                      {Object.keys(testCaseResult)?.length > 0 && (
                        <>
                          {Object.keys(testCaseResult).includes(
                            testcase?.id
                          ) ? (
                            <>
                              <Tooltip
                                message={` ${
                                  statusIconMap.find(
                                    (view) =>
                                      view.status ===
                                      testCaseResult?.[testcase?.id]?.status
                                  )?.tooltipMessage
                                }`}
                                position="w-[7rem] -mt-5 -left-7"
                              >
                                <GetHeroIcon
                                  name={
                                    statusIconMap.find(
                                      (view) =>
                                        view.status ===
                                        testCaseResult?.[testcase?.id]?.status
                                    )?.iconName || "XMarkIcon"
                                  }
                                  variant={
                                    statusIconMap.find(
                                      (view) =>
                                        view.status ===
                                        testCaseResult?.[testcase?.id]?.status
                                    )?.variant || "outline"
                                  }
                                  className={`size-5 ${
                                    statusIconMap.find(
                                      (view) =>
                                        view.status ===
                                        testCaseResult?.[testcase?.id]?.status
                                    )?.color || "text-red-800"
                                  }`}
                                />
                              </Tooltip>
                            </>
                          ) : (
                            <LoaderIcon />
                          )}
                        </>
                      )}

                      <span>
                        {t("assessment.coding.testCase", { index: index + 1 })}
                      </span>
                    </div>
                  ))}
              </div>

              <div
                className={cn(
                  "flex-1 min-h-0 thin-scrollbar overflow-y-auto p-4 sm:p-5 lg:border-l",
                  editorTheme === "vs-dark" && "border-gray-500 text-white",
                  editorTheme === "light" &&
                    "border-gray-200 dark:border-neutral-800 text-neutral-800"
                )}
              >
                <div>
                  {Object.keys(testCaseResult)?.length > 0 &&
                    Object.keys(testCaseResult).includes(
                      selectedTestCase?.id
                    ) &&
                    testCaseResult?.[selectedTestCase?.id]?.status ===
                      "TIMEOUT" && (
                      <>
                        <div className="text-red-600 text-xl font-bold mb-2">
                          {t("assessment.coding.timeLimitExceededTitle")}
                        </div>
                        <div className="text-gray-500 mb-6">
                          {t("assessment.coding.timeLimitExceededDescription")}
                        </div>
                      </>
                    )}
                </div>
                <div className="space-y-2">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      editorTheme === "vs-dark" && " text-white",
                      editorTheme === "light" && " text-neutral-800"
                    )}
                  >
                    {t("assessment.coding.input")}
                  </span>
                  <p
                    className={cn(
                      "w-full max-w-full rounded p-4 whitespace-pre-wrap break-words sm:w-fit",
                      editorTheme === "vs-dark" && "text-white bg-secondary",
                      editorTheme === "light" && "bg-[#f5f5f5]"
                    )}
                  >
                    {selectedTestCase?.input}
                  </p>
                </div>
                <div className="flex flex-col justify-between gap-5 mt-10 lg:flex-row">
                  {/* Expected */}
                  <div className="space-y-2">
                    <span
                      className={cn(
                        "text-sm font-medium",
                        editorTheme === "vs-dark" && " text-white",
                        editorTheme === "light" && " text-neutral-800"
                      )}
                    >
                      {t("assessment.coding.expectedOutput")}
                    </span>
                    <p
                      className={cn(
                        "max-w-full rounded p-4 whitespace-pre-wrap break-words w-full sm:w-fit",
                        editorTheme === "vs-dark" && "text-white bg-secondary",
                        editorTheme === "light" && "bg-[#f5f5f5]"
                      )}
                    >
                      {selectedTestCase?.output}
                    </p>
                  </div>
                  {/* Actual */}
                  {Object.keys(testCaseResult)?.length > 0 &&
                    Object.keys(testCaseResult).includes(
                      selectedTestCase?.id
                    ) && (
                      <div className="space-y-2">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            editorTheme === "vs-dark" && " text-white",
                            editorTheme === "light" && " text-neutral-800"
                          )}
                        >
                          {t("assessment.coding.yourOutput")}
                        </span>
                        <p
                          className={cn(
                            "min-w-32 max-w-full rounded p-4 whitespace-pre-wrap break-words w-full sm:w-auto sm:max-w-fit",
                            editorTheme === "vs-dark" &&
                              " text-white bg-secondary",
                            editorTheme === "light" && "bg-[#f5f5f5]"
                          )}
                        >
                          {
                            testCaseResult?.[selectedTestCase?.id]
                              ?.compileroutput
                          }
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-full min-h-0 flex-[0_0_38%] space-y-3 overflow-y-auto px-5 py-3 thin-scrollbar lg:w-[12rem] lg:flex-[0_0_12rem]">
                {hiddenTestCaseIds &&
                  hiddenTestCaseIds?.length > 0 &&
                  hiddenTestCaseIds.map((testcase: any, index: number) => (
                    <div
                      className={`flex items-center w-40 gap-1.5 px-5 py-1 rounded-md  cursor-pointer
                
                `}
                    >
                      {showLoader &&
                        Object.keys(testCaseResult)?.length == 0 && (
                          <div>
                            <LoaderIcon />
                          </div>
                        )}
                      {Object.keys(testCaseResult)?.length > 0 && (
                        <>
                          {Object.keys(testCaseResult).includes(testcase) ? (
                            <>
                              <Tooltip
                                message={` ${
                                  statusIconMap.find(
                                    (view) =>
                                      view.status ===
                                      testCaseResult?.[testcase]?.status
                                  )?.tooltipMessage
                                }`}
                                position="w-[7rem] -mt-5 -left-7"
                              >
                                {" "}
                                <GetHeroIcon
                                  name={
                                    statusIconMap.find(
                                      (view) =>
                                        view.status ===
                                        testCaseResult?.[testcase]?.status
                                    )?.iconName || "XMarkIcon"
                                  }
                                  variant={
                                    statusIconMap.find(
                                      (view) =>
                                        view.status ===
                                        testCaseResult?.[testcase]?.status
                                    )?.variant || "outline"
                                  }
                                  className={`size-5 ${
                                    statusIconMap.find(
                                      (view) =>
                                        view.status ===
                                        testCaseResult?.[testcase]?.status
                                    )?.color || "text-red-800"
                                  }`}
                                />
                              </Tooltip>
                            </>
                          ) : (
                            <LoaderIcon />
                          )}
                        </>
                      )}
                      <span>
                        {t("assessment.coding.testCase", { index: index + 1 })}
                      </span>
                    </div>
                  ))}
              </div>

              <div
                className={cn(
                  "flex flex-1 min-h-0 flex-col justify-center p-5 lg:border-l",
                  editorTheme === "vs-dark" && "border-gray-500 text-white",
                  editorTheme === "light" &&
                    "border-gray-200 dark:border-slate-800 text-neutral-800"
                )}
              >
                <div className="flex text-xl gap-2 items-center justify-center">
                  <GetHeroIcon
                    name="LockClosedIcon"
                    variant="outline"
                    className="w-5 h-5 mt-1"
                  />
                  <div>{t("assessment.coding.hiddenTestCase")}</div>
                </div>
                <div className="text-sm mt-2 text-center">
                  {t("assessment.coding.hiddenTestCaseDescription")}
                </div>
              </div>
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default EditorTestCases;
